import { elementAlign, font, fontSize, fontStyle, fontWeight, startingPoint, textDecoration } from "../lib/cssHelpers";

import type { SDK_Fonts } from "@quickpoint.me/shared";
import type { DefaultTheme } from "styled-components";

export const border = `
  ${(theme: DefaultTheme, baseFontSize: number) =>
    theme.border
      ? `${theme.border * baseFontSize}px solid ${theme.borderColor ? theme.borderColor : "#000"}`
      : "unset"}
`;

export const cornerRadius = `
  ${(theme: DefaultTheme, baseFontSize: number) =>
    theme.cornerRadius ? `${theme.cornerRadius * baseFontSize}px` : "unset"}
`;

export const lineHeight = `
  ${(theme: DefaultTheme) =>
    theme.lineHeight
      ? theme.lineHeight
      : "unset"}
`;

export const paddingBlockStart = `
  ${(theme: DefaultTheme, baseFontSize: number) =>
    theme.paddingBlockStart
      ? `${theme.paddingBlockStart * baseFontSize}px`
      : "unset"};
`;

export const paddingBlockEnd = `
  ${(theme: DefaultTheme, baseFontSize: number) =>
    theme.paddingBlockEnd
      ? `${theme.paddingBlockEnd * baseFontSize}px`
      : "unset"};
`;

export const paddingInlineStart = `
  ${(theme: DefaultTheme, baseFontSize: number) =>
    theme.paddingInlineStart
      ? `${theme.paddingInlineStart * baseFontSize}px`
      : "unset"};
`;

export const paddingInlineEnd = `
  ${(theme: DefaultTheme, baseFontSize: number) =>
    theme.paddingInlineEnd
      ? `${theme.paddingInlineEnd * baseFontSize}px`
      : "unset"};
`;

interface MainCssRulesProp {
  baseFontSize: number;
  baseFontSizeRaw?: number; // base font size without font multiplier, for padding/borders
  fonts?: SDK_Fonts;
  theme: DefaultTheme | DefaultTheme[keyof DefaultTheme];
  fontMultiplier?: number;
}

interface DisplayElementsCssRulesProp {
  baseFontSize: number;
  baseFontSizeRaw?: number; // base font size without font multiplier, for padding/borders
  fonts?: SDK_Fonts;
  isInLandscape: boolean;
  isOverflowing: boolean;
  stickyHeaderHeight: number;
  theme: DefaultTheme;
}

export function mainCssRules(props: MainCssRulesProp) {
  // Use baseFontSizeRaw for padding/borders (doesn't scale with font multiplier)
  const spacingFontSize = props.baseFontSizeRaw ?? props.baseFontSize;

  function getMainCssRules(
    parts: TemplateStringsArray,
    ...exp: ((arg0: DefaultTheme | DefaultTheme[keyof DefaultTheme]) => string)[]
  ) {
    const res = parts.reduce((prev, _curr, idx: number) => {
      return `${prev} ${exp[idx - 1](props.theme)} ${parts[idx]}`;
    });
    return res;
  }

  let css = getMainCssRules`
    ${(theme) => elementAlign(theme.align, theme.isInCell)};
    align-items: ${(theme: DefaultTheme) => theme.align};
    background: ${(theme: DefaultTheme) => theme.background ? theme.background : "unset"};
    border-radius: ${(theme: DefaultTheme) =>
      theme.cornerRadius ? `${theme.cornerRadius * spacingFontSize}px` : "unset"};
    border-block-end: ${(theme: DefaultTheme) =>
      theme.borderBlockEnd
        ? `${theme.borderBlockEnd * spacingFontSize}px solid`
        : "unset"};
    border-block-start: ${(theme: DefaultTheme) =>
      theme.borderBlockStart
        ? `${theme.borderBlockStart * spacingFontSize}px solid`
        : "unset"};
    border-inline-start: ${(theme: DefaultTheme) =>
      theme.borderInlineStart
        ? `${theme.borderInlineStart * spacingFontSize}px solid`
        : "unset"};
    border-inline-end: ${(theme: DefaultTheme) =>
      theme.borderInlineEnd
        ? `${theme.borderInlineEnd * spacingFontSize}px solid`
        : "unset"};
    border-color: ${(theme: DefaultTheme) => theme.borderColor ? theme.borderColor : "unset"};
    color: ${(theme: DefaultTheme) => theme.color ? theme.color : "unset"};
    font-family: ${(theme: DefaultTheme) => font(theme, props.fonts)};
    font-size:  ${(theme: DefaultTheme) => fontSize(theme, props.baseFontSize)};
    font-style: ${(theme: DefaultTheme) => fontStyle(theme.fontStyle)};
    font-weight: ${(theme: DefaultTheme) => fontWeight(theme.weight)};
    line-height: ${(theme: DefaultTheme) => theme.lineHeight ? theme.lineHeight : "unset"};
    padding-block-end: ${(theme: DefaultTheme) =>
      theme.paddingBlockEnd
        ? `${theme.paddingBlockEnd * spacingFontSize}px`
        : "unset"};
    padding-block-start: ${(theme: DefaultTheme) =>
      theme.paddingBlockStart
        ? `${theme.paddingBlockStart * spacingFontSize}px`
        : "unset"};
    padding-inline-start: ${(theme: DefaultTheme) =>
      theme.paddingInlineStart
        ? `${theme.paddingInlineStart * spacingFontSize}px`
        : "unset"};
    padding-inline-end: ${(theme: DefaultTheme) =>
      theme.paddingInlineEnd
        ? `${theme.paddingInlineEnd * spacingFontSize}px`
        : "unset"};
    text-decoration: ${(theme: DefaultTheme) => textDecoration(theme.decoration)};
    text-align: ${(theme: DefaultTheme) => theme.align ? theme.align : "unset"};
    white-space: pre-wrap;
`;

  // WCAG 1.4.10: Add word-breaking when font is enlarged to prevent horizontal scroll
  if (props.fontMultiplier && props.fontMultiplier > 1) {
    css += `
    overflow-wrap: anywhere;
    word-break: break-word;
    hyphens: auto;
`;
  }

  return css;
}

export function displayElementsCssRules(props: DisplayElementsCssRulesProp) {
  // Use baseFontSizeRaw for padding/borders (doesn't scale with font multiplier)
  const spacingFontSize = props.baseFontSizeRaw ?? props.baseFontSize;

  function cssRules(
    parts: TemplateStringsArray,
    ...exp: ((arg0: DefaultTheme | DefaultTheme[keyof DefaultTheme]) => string)[]
  ) {
    const res = parts.reduce((prev, _curr, idx: number) => {
      return `${prev} ${exp[idx - 1](props.theme)} ${parts[idx]}`;
    });
    return res;
  }
  const css = cssRules`
    align-items: ${(theme: DefaultTheme) => theme.align};
    border-radius: ${(theme: DefaultTheme) =>
      theme.cornerRadius ? `${theme.cornerRadius * spacingFontSize}px` : "unset"};
    border-block-end: ${(theme: DefaultTheme) =>
      theme.borderBlockEnd
        ? `${theme.borderBlockEnd * spacingFontSize}px solid ${theme.borderColor}`
        : "unset"}  ;
    border-block-start: ${(theme: DefaultTheme) =>
      theme.borderBlockStart
        ? `${theme.borderBlockStart * spacingFontSize}px solid${theme.borderColor}`
        : "unset"};
    border-inline-start: ${(theme: DefaultTheme) =>
      theme.borderInlineStart
        ? `${theme.borderInlineStart * spacingFontSize}px solid${theme.borderColor}`
        : "unset"};
    border-inline-end: ${(theme: DefaultTheme) =>
      theme.borderInlineEnd
        ? `${theme.borderInlineEnd * spacingFontSize}px solid${theme.borderColor}`
        : "unset"};
    color: ${(theme: DefaultTheme) => theme.color ? theme.color : "unset"};
    font-family: ${(theme: DefaultTheme) => font(theme, props.fonts)};
    font-size:  ${(theme: DefaultTheme) => fontSize(theme, props.baseFontSize)};
    font-style: ${(theme: DefaultTheme) => fontStyle(theme.fontStyle)};
    font-weight: ${(theme: DefaultTheme) => fontWeight(theme.weight)};
    justify-content: ${theme => startingPoint(theme.startingPoint, props.isOverflowing, props.isInLandscape)};
    line-height: ${(theme: DefaultTheme) => theme.lineHeight ? theme.lineHeight : "unset"};
    padding-block-end: ${(theme: DefaultTheme) =>
      theme.paddingBlockEnd && props.isInLandscape
        ? `${theme.paddingBlockEnd * spacingFontSize}px`
        : "unset"};
    padding-block-start: ${(theme: DefaultTheme) =>
      theme.paddingBlockStart && props.isInLandscape
        ? `${theme.paddingBlockStart * spacingFontSize + + props.stickyHeaderHeight}px`
        : "unset"};
    padding-inline-start: ${(theme: DefaultTheme) =>
      theme.paddingInlineStart
        ? `${theme.paddingInlineStart * spacingFontSize}px`
        : "unset"};
    padding-inline-end: ${(theme: DefaultTheme) =>
      theme.paddingInlineEnd
        ? `${theme.paddingInlineEnd * spacingFontSize}px`
        : "unset"};
    text-decoration: ${(theme: DefaultTheme) => textDecoration(theme.decoration)};
    white-space: pre-wrap;
        `;
  return css;
}
